
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { CalendarEvent, DateTimeRange, ScheduleAssistantResponse } from '@/types/calendar';
import { BrainCircuit } from 'lucide-react';
import { generateScheduleSuggestions } from './aiScheduler';
import EventDetailsForm from './components/EventDetailsForm';
import PreferencesForm from './components/PreferencesForm';
import SuggestionsDisplay from './components/SuggestionsDisplay';

interface ScheduleAssistantProps {
  events: CalendarEvent[];
  onEventAdd: (event: CalendarEvent) => void;
}

const ScheduleAssistant: React.FC<ScheduleAssistantProps> = ({ events, onEventAdd }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventType, setEventType] = useState('meeting');
  const [eventDescription, setEventDescription] = useState('');
  const [duration, setDuration] = useState('60');
  const [participants, setParticipants] = useState<string[]>([]);
  const [participantInput, setParticipantInput] = useState('');
  const [preferredDays, setPreferredDays] = useState<string[]>([]);
  const [preferredTimeRange, setPreferredTimeRange] = useState('any');
  const [suggestions, setSuggestions] = useState<ScheduleAssistantResponse | null>(null);
  
  const handleAddParticipant = () => {
    if (participantInput && !participants.includes(participantInput)) {
      setParticipants([...participants, participantInput]);
      setParticipantInput('');
    }
  };
  
  const handleRemoveParticipant = (participant: string) => {
    setParticipants(participants.filter(p => p !== participant));
  };
  
  const togglePreferredDay = (day: string) => {
    if (preferredDays.includes(day)) {
      setPreferredDays(preferredDays.filter(d => d !== day));
    } else {
      setPreferredDays([...preferredDays, day]);
    }
  };
  
  const handleGetSuggestions = async () => {
    if (!eventTitle) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await generateScheduleSuggestions(
        events,
        {
          title: eventTitle,
          type: eventType,
          description: eventDescription,
          durationMinutes: parseInt(duration),
          participants,
          preferredDays,
          preferredTimeRange
        }
      );
      
      setSuggestions(result);
    } catch (error) {
      console.error('Error getting scheduling suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleScheduleEvent = (timeSlot: DateTimeRange) => {
    const newEvent: CalendarEvent = {
      id: `event-${Date.now()}`,
      title: eventTitle,
      description: eventDescription,
      start: timeSlot.start,
      end: timeSlot.end,
      source: 'local',
      calendarId: 'local',
      attendees: participants.map(p => ({ email: p }))
    };
    
    onEventAdd(newEvent);
    
    // Reset form
    handleResetForm();
  };
  
  const handleResetForm = () => {
    setEventTitle('');
    setEventType('meeting');
    setEventDescription('');
    setDuration('60');
    setParticipants([]);
    setPreferredDays([]);
    setPreferredTimeRange('any');
    setSuggestions(null);
  };
  
  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <BrainCircuit className="h-5 w-5 text-primary" />
        <h2 className="text-xl font-semibold">AI Schedule Assistant</h2>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Event Details</CardTitle>
            <CardDescription>Describe the event you want to schedule</CardDescription>
          </CardHeader>
          <CardContent>
            <EventDetailsForm 
              eventTitle={eventTitle}
              setEventTitle={setEventTitle}
              eventType={eventType}
              setEventType={setEventType}
              eventDescription={eventDescription}
              setEventDescription={setEventDescription}
              duration={duration}
              setDuration={setDuration}
              participants={participants}
              participantInput={participantInput}
              setParticipantInput={setParticipantInput}
              handleAddParticipant={handleAddParticipant}
              handleRemoveParticipant={handleRemoveParticipant}
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Preferences</CardTitle>
            <CardDescription>Set your scheduling preferences</CardDescription>
          </CardHeader>
          <CardContent>
            <PreferencesForm 
              preferredDays={preferredDays}
              togglePreferredDay={togglePreferredDay}
              preferredTimeRange={preferredTimeRange}
              setPreferredTimeRange={setPreferredTimeRange}
              handleGetSuggestions={handleGetSuggestions}
              handleResetForm={handleResetForm}
              isLoading={isLoading}
              isFormValid={!!eventTitle}
            />
          </CardContent>
        </Card>
      </div>
      
      {suggestions && (
        <SuggestionsDisplay 
          suggestions={suggestions} 
          onSchedule={handleScheduleEvent} 
        />
      )}
    </div>
  );
};

export default ScheduleAssistant;
